// The avatar crop overlay (ADR-0022) — a HOST-OWNED sheet, like
// camera-sheet.ts, and legitimate for the same reason its header gives:
// imperative raw DOM is allowed in the TRUSTED shell even though mini-apps
// may never touch it. A pinch-and-drag circular crop needs pointer events and
// a canvas, which the declarative catalog deliberately does not offer
// untrusted code.
//
// The arithmetic mirrors the shared Kotlin `AvatarCrop` (cover the square,
// zoom about the centre, clamp the pan so every pixel of the circle is
// picture), and the output is the two JPEG renditions the server validates:
// 640×640 under 256 KB and 192×192 under 64 KB, EXIF-free by construction —
// a canvas re-encode carries none.

import { hostTranslator } from "../joana/i18n/index.ts";

export interface AvatarCropResult {
  full: Blob;
  thumb: Blob;
}

const FULL_EDGE = 640;
const THUMB_EDGE = 192;
const FULL_MAX = 256 * 1024;
const THUMB_MAX = 64 * 1024;
const SAFE_ZONE = 0.8;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

interface CropState {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

export class AvatarEditorSheet {
  /** Pick a file, crop it, and answer both renditions — or null on cancel. */
  async pickAndCrop(showVisibility: { policy: string } | null): Promise<
    { result: AvatarCropResult; policy: string } | null
  > {
    const file = await this.pickFile();
    if (!file) return null;
    const image = await this.decode(file);
    if (!image) return null;
    return this.crop(image, showVisibility);
  }

  private pickFile(): Promise<File | null> {
    return new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = () => resolve(input.files?.[0] ?? null);
      // Some browsers never fire change on cancel; a focus check is the
      // pragmatic fallback.
      window.addEventListener(
        "focus",
        () => setTimeout(() => resolve(input.files?.[0] ?? null), 400),
        { once: true },
      );
      input.click();
    });
  }

  private async decode(file: File): Promise<ImageBitmap | null> {
    try {
      // createImageBitmap applies EXIF orientation, which is exactly the
      // "bake it into the pixels" step the other platforms perform.
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      return null;
    }
  }

  private crop(
    image: ImageBitmap,
    visibility: { policy: string } | null,
  ): Promise<{ result: AvatarCropResult; policy: string } | null> {
    return new Promise((resolve) => {
      const overlay = document.createElement("div");
      overlay.style.cssText =
        "position:fixed;inset:0;background:rgba(0,0,0,.75);z-index:10000;display:flex;align-items:center;justify-content:center;";
      const panel = document.createElement("div");
      panel.style.cssText =
        "background:#1b1b20;border-radius:16px;padding:16px;max-width:420px;width:92vw;color:#fff;font-family:system-ui;";
      panel.innerHTML = `
        <div style="font-size:15px;margin-bottom:10px">Drag to move, scroll or pinch to zoom. What is inside the circle is the photo.</div>
        <div data-stage style="position:relative;width:100%;aspect-ratio:1;overflow:hidden;border-radius:8px;background:#000;touch-action:none;cursor:grab"></div>
        ${visibility ? `
        <label style="display:flex;align-items:center;gap:8px;margin-top:12px;font-size:14px">
          Who can see this
          <select data-policy style="flex:1;background:#2a2a31;color:#fff;border:none;padding:8px;border-radius:8px">
            <option value="everyone">Everyone</option>
            <option value="contacts">People I chat with</option>
            <option value="nobody">Nobody</option>
          </select>
        </label>` : ""}
        <div style="display:flex;gap:10px;margin-top:14px;justify-content:flex-end">
          <button data-cancel style="background:transparent;color:#bbb;border:none;padding:10px 14px;font-size:15px;cursor:pointer">Cancel</button>
          <button data-use style="background:linear-gradient(135deg,#FF8A3D,#FF3D77);color:#fff;border:none;padding:10px 18px;border-radius:10px;font-size:15px;font-weight:600;cursor:pointer">Use photo</button>
        </div>`;
      overlay.appendChild(panel);
      document.body.appendChild(overlay);

      const stage = panel.querySelector("[data-stage]") as HTMLElement;
      const canvas = document.createElement("canvas");
      canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%";
      stage.appendChild(canvas);
      const policySelect = panel.querySelector("[data-policy]") as HTMLSelectElement | null;
      if (policySelect && visibility) policySelect.value = visibility.policy;

      const state: CropState = { zoom: 1, offsetX: 0, offsetY: 0 };
      let viewport = 1;

      const clamp = () => {
        const scale = (viewport / Math.min(image.width, image.height)) * state.zoom;
        const maxX = Math.max(0, (image.width * scale - viewport) / 2);
        const maxY = Math.max(0, (image.height * scale - viewport) / 2);
        state.offsetX = Math.min(maxX, Math.max(-maxX, state.offsetX));
        state.offsetY = Math.min(maxY, Math.max(-maxY, state.offsetY));
      };

      const draw = () => {
        const dpr = window.devicePixelRatio || 1;
        viewport = stage.clientWidth;
        canvas.width = viewport * dpr;
        canvas.height = viewport * dpr;
        const ctx = canvas.getContext("2d")!;
        ctx.scale(dpr, dpr);
        clamp();
        const scale = (viewport / Math.min(image.width, image.height)) * state.zoom;
        const w = image.width * scale;
        const h = image.height * scale;
        ctx.clearRect(0, 0, viewport, viewport);
        ctx.drawImage(image, (viewport - w) / 2 + state.offsetX, (viewport - h) / 2 + state.offsetY, w, h);
        // Dim outside the circle + the safe-zone ring.
        ctx.save();
        ctx.fillStyle = "rgba(0,0,0,.55)";
        ctx.beginPath();
        ctx.rect(0, 0, viewport, viewport);
        ctx.arc(viewport / 2, viewport / 2, viewport / 2, 0, Math.PI * 2, true);
        ctx.fill("evenodd");
        ctx.strokeStyle = "rgba(255,255,255,.9)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(viewport / 2, viewport / 2, viewport / 2 - 1, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = "rgba(255,255,255,.35)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(viewport / 2, viewport / 2, (viewport / 2) * SAFE_ZONE, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      };

      // Pointer pan + wheel/pinch zoom.
      let dragging = false;
      let lastX = 0;
      let lastY = 0;
      const pointers = new Map<number, { x: number; y: number }>();
      let pinchDist = 0;

      stage.addEventListener("pointerdown", (e) => {
        stage.setPointerCapture(e.pointerId);
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 1) {
          dragging = true;
          lastX = e.clientX;
          lastY = e.clientY;
        } else if (pointers.size === 2) {
          const [a, b] = [...pointers.values()];
          pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
        }
      });
      stage.addEventListener("pointermove", (e) => {
        if (!pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 2) {
          const [a, b] = [...pointers.values()];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (pinchDist > 0) {
            const factor = dist / pinchDist;
            state.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, state.zoom * factor));
            state.offsetX *= factor;
            state.offsetY *= factor;
          }
          pinchDist = dist;
        } else if (dragging) {
          state.offsetX += e.clientX - lastX;
          state.offsetY += e.clientY - lastY;
          lastX = e.clientX;
          lastY = e.clientY;
        }
        draw();
      });
      const release = (e: PointerEvent) => {
        pointers.delete(e.pointerId);
        if (pointers.size < 2) pinchDist = 0;
        if (pointers.size === 0) dragging = false;
      };
      stage.addEventListener("pointerup", release);
      stage.addEventListener("pointercancel", release);
      stage.addEventListener("wheel", (e) => {
        e.preventDefault();
        const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
        state.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, state.zoom * factor));
        state.offsetX *= factor;
        state.offsetY *= factor;
        draw();
      });

      const close = (value: { result: AvatarCropResult; policy: string } | null) => {
        image.close();
        overlay.remove();
        resolve(value);
      };
      (panel.querySelector("[data-cancel]") as HTMLElement).onclick = () => close(null);
      (panel.querySelector("[data-use]") as HTMLElement).onclick = () => {
        void (async () => {
          clamp();
          const result = await renderRenditions(image, state, viewport);
          if (!result) return close(null);
          close({ result, policy: policySelect?.value ?? "everyone" });
        })();
      };

      draw();
    });
  }
}

async function renderRenditions(
  image: ImageBitmap,
  state: CropState,
  viewport: number,
): Promise<AvatarCropResult | null> {
  // The crop rect, in source pixels — the same arithmetic as AvatarCrop.rect.
  const scale = (viewport / Math.min(image.width, image.height)) * state.zoom;
  const drawnW = image.width * scale;
  const drawnH = image.height * scale;
  const leftInView = (viewport - drawnW) / 2 + state.offsetX;
  const topInView = (viewport - drawnH) / 2 + state.offsetY;
  const size = Math.max(1, Math.min(Math.round(viewport / scale), Math.min(image.width, image.height)));
  const x = Math.min(Math.max(0, Math.round(-leftInView / scale)), image.width - size);
  const y = Math.min(Math.max(0, Math.round(-topInView / scale)), image.height - size);

  const full = await encode(image, x, y, size, FULL_EDGE, FULL_MAX);
  const thumb = await encode(image, x, y, size, THUMB_EDGE, THUMB_MAX);
  if (!full || !thumb) return null;
  return { full, thumb };
}

async function encode(
  image: ImageBitmap,
  x: number,
  y: number,
  size: number,
  edge: number,
  maxBytes: number,
): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = edge;
  canvas.height = edge;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, x, y, size, size, 0, 0, edge, edge);
  // Step the quality down until it fits — a 422 from the server is the wrong
  // place to learn a busy photo does not.
  for (let q = 0.82; q >= 0.35; q -= 0.08) {
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", q));
    if (blob && blob.size <= maxBytes) return blob;
    if (q - 0.08 < 0.35 && blob) return blob;
  }
  return null;
}

// --- Simple grid pickers (emoji / sticker / frame), same trusted-shell rules.

export interface GridChoice {
  key: string;
  label?: string;
  imageUrl?: string;
  emoji?: string;
}

export function pickFromGrid(title: string, choices: GridChoice[]): Promise<string | null> {
  const t = hostTranslator();
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,.75);z-index:10000;display:flex;align-items:center;justify-content:center;";
    const panel = document.createElement("div");
    panel.style.cssText =
      "background:#1b1b20;border-radius:16px;padding:16px;max-width:460px;width:92vw;max-height:70vh;overflow:auto;color:#fff;font-family:system-ui;";
    const heading = document.createElement("div");
    heading.style.cssText = "font-size:16px;font-weight:600;margin-bottom:12px";
    heading.textContent = title;
    const grid = document.createElement("div");
    grid.style.cssText = "display:grid;grid-template-columns:repeat(auto-fill,minmax(64px,1fr));gap:10px";
    const cancel = document.createElement("button");
    cancel.style.cssText =
      "margin-top:14px;background:transparent;color:#bbb;border:none;padding:8px 12px;font-size:15px;cursor:pointer";
    cancel.textContent = t("Cancel");
    const close = (value: string | null) => {
      overlay.remove();
      resolve(value);
    };
    cancel.onclick = () => close(null);
    for (const c of choices) {
      const cell = document.createElement("button");
      cell.style.cssText =
        "background:#2a2a31;border:none;border-radius:12px;padding:6px;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:4px;color:#ddd;font-size:11px";
      if (c.imageUrl) {
        const img = document.createElement("img");
        img.src = c.imageUrl; // trusted shell; same-origin bundled art only
        img.style.cssText = "width:52px;height:52px;object-fit:contain";
        cell.appendChild(img);
      } else if (c.emoji) {
        const span = document.createElement("span");
        span.style.cssText = "font-size:30px";
        span.textContent = c.emoji;
        cell.appendChild(span);
      }
      if (c.label) {
        const label = document.createElement("span");
        label.textContent = c.label;
        cell.appendChild(label);
      }
      cell.onclick = () => close(c.key);
      grid.appendChild(cell);
    }
    panel.appendChild(heading);
    panel.appendChild(grid);
    panel.appendChild(cancel);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
  });
}

/** Ask for a line of text (the display-name edit). Prompt-shaped but themed. */
export function askText(title: string, initial: string): Promise<string | null> {
  // The CALLER translates the title; these two buttons are the sheet's own
  // words, and until s147 they were the only English left on a screen
  // otherwise wholly in Kiswahili — the caller had been translated and its
  // chrome had not.
  const t = hostTranslator();
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,.75);z-index:10000;display:flex;align-items:center;justify-content:center;";
    const panel = document.createElement("div");
    panel.style.cssText =
      "background:#1b1b20;border-radius:16px;padding:16px;max-width:380px;width:92vw;color:#fff;font-family:system-ui;display:flex;flex-direction:column;gap:12px";
    const heading = document.createElement("div");
    heading.style.cssText = "font-size:16px;font-weight:600";
    heading.textContent = title;
    const input = document.createElement("input");
    input.value = initial;
    input.style.cssText = "background:#2a2a31;color:#fff;border:none;padding:10px;border-radius:8px;font-size:15px";
    const row = document.createElement("div");
    row.style.cssText = "display:flex;justify-content:flex-end;gap:10px";
    const cancel = document.createElement("button");
    cancel.textContent = t("Cancel");
    cancel.style.cssText = "background:transparent;color:#bbb;border:none;padding:8px 12px;cursor:pointer";
    const save = document.createElement("button");
    save.textContent = t("Save");
    save.style.cssText =
      "background:linear-gradient(135deg,#FF8A3D,#FF3D77);color:#fff;border:none;padding:8px 16px;border-radius:8px;font-weight:600;cursor:pointer";
    const close = (v: string | null) => {
      overlay.remove();
      resolve(v);
    };
    cancel.onclick = () => close(null);
    save.onclick = () => close(input.value);
    input.onkeydown = (e) => {
      if (e.key === "Enter") close(input.value);
      if (e.key === "Escape") close(null);
    };
    row.appendChild(cancel);
    row.appendChild(save);
    panel.appendChild(heading);
    panel.appendChild(input);
    panel.appendChild(row);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    input.focus();
  });
}
